import React, { useContext, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import Title from "../../components/title";
import Text from "../../components/text";
import NewsCardHome from "../../components/newsCardHome";
import ApiNoticias from "../../services/ApiNoticias";
import { IDataNoticias } from "../../@interfaces";
import { CustumerContext } from "../../contexts/custumer";
import Post from "../../components/post";
import BoletoCardHome from "../../components/boletosCardHome";
import { Boleto } from "../../@types";
import APIFinanceiro from "../../services/APIFinanceiro";
import ToastContext from "../../contexts/toast";
import Subtitle from "../../components/subtitle";
import Skeleton from "../../components/skeleton";

const Home: React.FC = () => {

  const [newsList, setNewsList] = useState<IDataNoticias[]>([]);
  const [newsListToShow, setNewsListToShow] = useState<IDataNoticias[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingError, setLoadingError] = useState(false);
  const [openPost, setOpenPost] = useState<IDataNoticias | null>(null);
  const [boletos, setBoletos] = useState<Boleto[]>([]);
  const [boletosLoading, setBoletosLoading] = useState(true);
  const [boletosError, setBoletosError] = useState(false);

  const { id_current_custumer } = useContext(CustumerContext);
  const { showErrorToast } = useContext(ToastContext);
  const history = useHistory();

  useEffect(() => {
    if (id_current_custumer) {
      loadNews();
      loadBoletos();
    }
  }, [id_current_custumer]);

  async function loadNews() {
    try {
      setIsLoading(true);
      setLoadingError(false);

      if (!id_current_custumer) return;

      const response = await ApiNoticias.getNoticias(
        1,
        id_current_custumer
      );

      if (response.error) {
        setLoadingError(true);
        return;
      }

      const noticias = response.noticias || [];

      setNewsList(noticias);
      setNewsListToShow(noticias.slice(0, 3));

    } catch (error) {
      console.error(error);
      setLoadingError(true);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadBoletos() {
    try {
      setBoletosLoading(true);
      setBoletosError(false);

      const { start, end } = getCurrentWeekRange();

      const response = await APIFinanceiro.getBoletos({
        dataInicial: start,
        dataFinal: end,
        nf: 0,
        cliente: id_current_custumer,
      });

      if (response.boletos) {

        const hoje = new Date();

        const parseDate = (dateStr: string) => {
          const [dia, mes, ano] = dateStr.split('/').map(Number);
          return new Date(ano, mes - 1, dia);
        };

        const boletosFiltrados = response.boletos
          .filter((b) => {
            const emissao = parseDate(b.dtEmimssao);
            return isThisWeek(emissao);
          })
          .slice()
          .sort((a: Boleto, b: Boleto) => {
            const dataA = parseDate(a.vencBoleto);
            const dataB = parseDate(b.vencBoleto);

            const hoje = new Date();

            const aVencido = dataA < hoje;
            const bVencido = dataB < hoje;

            if (aVencido && !bVencido) return -1;
            if (!aVencido && bVencido) return 1;

            return dataA.getTime() - dataB.getTime();
          });

        setBoletos(boletosFiltrados);

      } else {
        setBoletos([]);
      }

    } catch (e) {
      console.error(e);
      setBoletosError(true);
    } finally {
      setBoletosLoading(false);
    }
  }

  function getCurrentWeekRange(): { start: string; end: string } {
    const now = new Date();
    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    const toISO = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    return { start: toISO(monday), end: toISO(sunday) };
  }

  function isThisWeek(date: Date): boolean {
    const now = new Date();

    const day = now.getDay();
    const diffToMonday = day === 0 ? -6 : 1 - day;

    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    monday.setHours(0, 0, 0, 0);

    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    sunday.setHours(23, 59, 59, 999);

    return date >= monday && date <= sunday;
  }

  async function handleOpenBoleto(boleto: Boleto) {
    try {
      const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent);
      const isAndroid = /Android/i.test(navigator.userAgent);

      const response = await APIFinanceiro.getArquivoDownload({
        Arquivos: [Number(boleto.arquivo)]
      });

      const base64 = response?.listaArquivo?.[0]?.conteudo;

      if (!base64) {
        showErrorToast(response?.message || "Não foi possível carregar o boleto.");
        return;
      }

      const byteCharacters = atob(base64.split(",")[1]);
      const byteNumbers = Array.from(byteCharacters).map(c => c.charCodeAt(0));
      const byteArray = new Uint8Array(byteNumbers);

      const blob = new Blob([byteArray], { type: "application/pdf" });
      const pdfUrl = URL.createObjectURL(blob);

      if (isIOS) {
        const iosWindow = window.open("", "_blank");
        if (!iosWindow) return showErrorToast("Não foi possível abrir o PDF.");
        iosWindow.location.href = pdfUrl;
        return;
      }

      if (isAndroid) {
        const link = document.createElement("a");
        link.href = pdfUrl;
        link.target = "_blank";
        link.download = `${boleto.descEmpresa} - NF ${boleto.nF}.pdf`;

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        return;
      }

      window.open(pdfUrl, "_blank");

    } catch (error) {
      console.error(error);
      showErrorToast("Algo deu errado ao abrir o boleto.");
    }
  }

  const hoje = new Date();

  const diaSemana = hoje.toLocaleDateString('pt-BR', {
    weekday: 'long'
  });

  const dataCompleta = hoje.toLocaleDateString('pt-BR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric'
  });

  function openNews(post: IDataNoticias) {
    setOpenPost(post);
  }

  function closePost() {
    setOpenPost(null);
  }

  function goToNews() {
    history.push({ pathname: '/noticias', state: { newsList } });
  }

  function goToBoletos() {
    history.push('/financeiro?tab=boletos');
  }

  return (
    <>
      {!!openPost && <Post data={openPost} closePost={closePost} />}
      <div className="flex flex-col gap-10 max-[1024px]:gap-[25px]">
        <div className="flex flex-col rounded-[4px] px-3 py-1.5 justify-center max-[1024px]:mt-[15px] max-[1024px]:py-1" style={{ borderLeft: '5px solid #1566A4' }}>
          <Subtitle className="!font-semibold !text-[rgba(0,0,0,.4)] !text-[0.9375rem] max-[1024px]:!text-[0.8125rem]">{diaSemana.charAt(0).toUpperCase() + diaSemana.slice(1)}</Subtitle>
          <Title className="!font-bold !text-[1.375rem] max-[1024px]:!text-[1.125rem]">{dataCompleta}</Title>
        </div>

        <section className="flex w-full h-[415px] gap-[23.75px] max-[1024px]:flex-col max-[1024px]:h-full">

          {/* Card de notícias */}
          <div className="bg-white p-[10px] w-[65%] h-[415px] rounded-[12px] max-[1024px]:w-full">
            <header className="px-[15px] pt-[8px] pb-[16px] w-full !flex items-center justify-between" style={{ borderBottom: '1px solid #EBE6E7' }}>
              <Title className="!text-[0.9375rem] !font-bold">Últimas notícias</Title>
              <Text className="!text-[0.78125rem] !text-[#1566A4] !font-semibold !cursor-pointer hover:!underline" onClick={goToNews}>
                Ver todas
              </Text>
            </header>

            {isLoading ? (
              <div className="flex flex-col pt-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center gap-4 px-[15px] py-[14px]">
                    <Skeleton variant="rectangular" width={48} height={48} borderRadius={8} />
                    <div className="flex flex-col gap-1.5 flex-1">
                      <Skeleton variant="text" width="80%" height={18} />
                      <Skeleton variant="text" width="50%" height={14} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={`${(loadingError || newsListToShow.length === 0) ? 'h-[340px] flex flex-col justify-center items-center' : ''}`}>
                {newsListToShow.length > 0 && (
                  <>
                    <ul className="flex flex-col gap-2.5 w-full p-0">
                      {newsListToShow.map((item, index) => (
                        <li key={index}>
                          <NewsCardHome
                            news={item}
                            isLast={index === newsListToShow.length - 1}
                            onClick={() => openNews(item)}
                          />
                        </li>
                      ))}
                    </ul>
                    {newsListToShow.length < 3 && (
                      <Text className="!text-[rgba(0,0,0,.4)] !text-center !mt-7">Sem mais notícias no momento</Text>
                    )}
                  </>
                )}
                {newsListToShow.length === 0 && !loadingError && (
                  <div className="h-[300px] flex items-center justify-center">
                    <Text className="!text-[rgba(0,0,0,.4)] !text-center">Nenhuma notícia encontrada</Text>
                  </div>
                )}
                {loadingError && (
                  <div className="h-[300px] flex items-center justify-center">
                    <Text className="!text-[rgba(0,0,0,.4)]">
                      Algo deu errado no carregamento das notícias.
                      <a className="text-[#1566A4] hover:underline cursor-pointer" onClick={loadNews}> Tentar novamente?</a>
                    </Text>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Card de boletos */}
          <div className="bg-white p-[10px] w-[35%] h-[415px] rounded-[12px] max-[1024px]:w-full">
            <header className="px-[15px] pt-[8px] pb-[16px] w-full !flex items-center justify-between" style={{ borderBottom: '1px solid #EBE6E7' }}>
              <Title className="!text-[0.9375rem] !font-bold">Boletos semanais</Title>
              <Text className="!text-[0.78125rem] !text-[#1566A4] !font-semibold !cursor-pointer hover:!underline" onClick={goToBoletos}>
                Ver todos
              </Text>
            </header>

            {boletosLoading ? (
              <div className="flex flex-col pt-2">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="flex items-center justify-between px-[15px] py-[18px]">
                    <div className="flex items-center gap-4">
                      <Skeleton variant="rectangular" width={30} height={30} borderRadius={8} />
                      <div className="flex flex-col gap-1.5">
                        <Skeleton variant="text" width={120} height={16} />
                        <Skeleton variant="text" width={80} height={13} />
                      </div>
                    </div>
                    <Skeleton variant="circular" width={10} height={10} />
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col overflow-y-auto max-h-[340px] custom-scroll">
                {boletosError && (
                  <div className="h-[300px] flex items-center justify-center">
                    <Text className="!text-[rgba(0,0,0,.4)]">
                      Algo deu errado.
                      <a onClick={loadBoletos} className="text-[#1566A4] cursor-pointer hover:underline"> Tentar novamente?</a>
                    </Text>
                  </div>
                )}
                {!boletosError && boletos.length > 0 && (
                  boletos.map((boleto, index) => (
                    <BoletoCardHome
                      key={boleto.contasAReceber}
                      boleto={boleto}
                      isLast={index === Math.min(boletos.length, 4) - 1}
                      onClick={() => handleOpenBoleto(boleto)}
                    />
                  ))
                )}
                {boletos.length < 4 && boletos.length !== 0 && (
                  <Text className="!text-[rgba(0,0,0,.4)] !text-center !mt-7">Sem mais boletos nessa semana</Text>
                )}
                {!boletosError && boletos.length === 0 && (
                  <div className="h-[300px] flex items-center justify-center">
                    <Text className="!text-[rgba(0,0,0,.4)] !text-center">Nenhum boleto nessa semana</Text>
                  </div>
                )}
              </div>
            )}
          </div>

        </section>
      </div>
    </>
  );
}

export default Home;